<script>
import autocomplete from './autocomplete.vue';
import {defineComponent} from 'vue'

export default defineComponent ({
    name: "AllowanceTimecard",

    emits: ['update_override', 'update_assignment'],

    components: {
        autocomplete,
    },

    props: {
        name: {
            type: String,
            required: true,
        },
        label: {
            type: String,
            required: true,
        },
        amount: {
            type: [ String, Number ],
            required: false,
        },
        daysWorkedOverride: {
            type: [ String, Number ],
            required: false,
        },
        reference: {
            type: String,
            required: false,
            default: () => this.name,
        },
        brokenName: {
            type: String,
            required: true,
        },
        modelName: {
            type: String,
            required: true,
        },
        showCoding: {
            type: Boolean,
            required: false,
            default: true,
        },
        codeName: {
            type: String,
            required: true,
        },
        ffName: {
            type: String,
            required: true,
        },
        ff2Name: {
            type: String,
            required: true,
        },
        preselectedCode: {
            type: String,
            required: false,
        },
        preselectedFreeField: {
            type: String,
            required: false,
        },
        preselectedFreeField2: {
            type: String,
            required: false,
        },
        rateUnitType: {
            type: String,
            required: false,
            default: 'week',
        },
        enableProBooks: {
            type: Boolean,
            required: true,
        },
        probooksAccounts: {
            type: [ Array, String ],
            required: false,
        },
        probooksFreeFields: {
            type: [ Array, String ],
            required: false,
        },
        assignment: {
            type: Object,
            required: true,
        },
        daysWorked: {
            type: [ String, Number ],
            required: false,
        },
        daysWorkedLocked: {
            type: Boolean,
            required: false,
            default: false,
        },
        isNew: {
            type: Boolean,
            required: false,
            default: false,
        },
        readonly: {
            type: Boolean,
            required: false,
            default: false,
        },
        classes: {
            type: [ String, Array ],
            required: false,
            default: 'form-control'
        }
    },

    data() {
        return {
            iteration: 0,
            allowanceAmount: null,
            allowanceCode: null,
            allowanceFreeField: null,
            allowanceFreeField2: null,
            daysWorkedEntered: null,
            amountEntered: null,
        }
    },

    computed: {
        width() {
            // Reducing by 3 % to handle sigma-frontend navbar
            return '100%';
        },

        height() {
            // Reducing by 3 % to handle sigma-frontend navbar
            return window.innerHeight * 0.63 + 'px';
        },
        isWeekly () {
            // Default to weekly
            if (! this.rateUnitType || typeof this.rateUnitType !== 'string') return true;
            return this.rateUnitType.toLowerCase() === 'week' ? true : false;
        },
        isMatchingDaysWorked() {
            if (this.isWeekly) {
                return true;
            } else {
                return (!this.daysWorkedOverride || parseFloat(this.daysWorkedOverride) === parseFloat(this.daysWorked));
            }
        },
        isMatchingAmount() {
            if (!this.assignment[this.modelName]) {
                return true;
            } else if (this.isWeekly) {
                return parseFloat(this.assignment[this.modelName]) === parseFloat(this.adjustedAmount);
            } else {
                return (this.daysWorked === 0 && !this.adjustedAmount) || (this.daysWorked === null || (parseFloat(this.daysWorkedOverride || this.daysWorked) * this.assignment[this.modelName] === parseFloat(this.adjustedAmount)));
            }
        },
        adjustedAmount() {
            // console.log(this.isWeekly, this.isNew, this.amount, this.assignment[this.modelName]);
            if (this.amountEntered) {
                return this.amountEntered;
            } else if (this.isWeekly && this.isNew && !this.amount && this.assignment[this.modelName]) {
                this.updateAmountOverride(this.assignment[this.modelName]);
                return this.assignment[this.modelName];
            } else if (!this.isWeekly && !this.amount) {
                return;
            } else {
                return this.amount;
            }
        },
        startAmountWithUnit() {
            let amount = this.assignment[this.modelName] ? parseFloat(this.assignment[this.modelName]).toFixed(2) : null;
            return amount ? `${amount}${this.isWeekly ? '/W' : '/D'}` : 'N/A';
        }
    },

    methods: {
        createDialog(message) {
            this.modalMessage = message;
            this.showModal = true;
        },

        updateAmountOverride(event) {
            // console.log('updateAmountOverride', event, this.amountOverride);
            this.amountEntered = (event && event.target && event.target.value) ? event.target.value : (event || event === 0 ? event : 0 || 0) || 0;
            if (isNaN(this.amountEntered)) this.amountEntered = 0;
            else this.$emit('update_override', this.amountEntered, this.modelName, 'amount');
        },

        updateDaysWorkedOverride(event) {
            // console.log('updateDaysWorkedOverride', event, this.modelName, 'daysWorked')
            this.daysWorkedEntered = (event && event.target && event.target.value) ? event.target.value : (event || event === 0 ? event : this.daysWorkedEntered || null) || null;
            if (this.daysWorkedEntered && isNaN(this.daysWorkedEntered)) this.daysWorkedEntered = this.daysWorked;
            this.$emit('update_override', this.daysWorkedEntered, this.modelName, 'daysWorked');

            this.amountEntered = this.daysWorkedEntered ? this.daysWorkedEntered * this.assignment[this.modelName] : null;
            this.updateAmountOverride(this.amountEntered);
        },

        updateCode(event) {
            if (this.enableProBooks) {
                this.$emit('update_assignment', event, this.codeName);
            } else if (event.target && event.target.value) {
                this.$emit('update_assignment', event.target.value, this.codeName);
            } else {
                this.$emit('update_assignment', this.allowanceCode, this.codeName);
            }
        },

        updateFreeField(event) {
            if (this.enableProBooks) {
                this.$emit('update_assignment', event, this.ffName);
            } else if (event.target && event.target.value) {
                this.$emit('update_assignment', event.target.value, this.ffName);
            } else {
                this.$emit('update_assignment', this.allowanceFreeField, this.ffName);
            }
        },

        updateFreeField2(event) {
            if (this.enableProBooks) {
                this.$emit('update_assignment', event, this.ff2Name);
            } else if (event.target && event.target.value) {
                this.$emit('update_assignment', event.target.value, this.ff2Name);
            } else {
                this.$emit('update_assignment', this.allowanceFreeField2, this.ffName2);
            }
        },
    },

    created() {
        if (! this.allowanceCode && this.preselectedCode) this.allowanceCode = this.preselectedCode;
        if (! this.allowanceCode && this.assignment[this.codeName]) this.allowanceCode = this.assignment[this.codeName];
    },
});
</script>

<template>
<div>
    <div class="col-sm-3" style="padding-right: 0px"><label :for="name">{{ label }}&nbsp;</label></div>
    <div class="col-sm-9 timecard">
        <div :class="showCoding ? 'col-sm-3' : 'col-sm-4'" style="padding-right: 0px"><input class="form-control text-right amount" type="text" :name="name" :value="startAmountWithUnit" placeholder="$ Start" readonly></div>
        <div v-if="isWeekly">
            <div :class="[ showCoding ? 'col-sm-3' : 'col-sm-8', isMatchingAmount ? '' : 'overridden' ]" style="padding-right: 0px"><input class="form-control text-right" type="text" :ref="modelName" :name="modelName + 'Amount'" placeholder="Amount" :value="adjustedAmount" @change="updateAmountOverride" :readonly="readonly" ></div>
        </div>
        <div v-else>
            <div :class="[ showCoding ? 'col-sm-2' : 'col-sm-3', isMatchingDaysWorked ? '' : 'overridden' ]" style="padding-right: 0px"><input class="form-control" type="text" :name="modelName + 'Worked'" :placeholder="daysWorked || 'Auto'" :value="daysWorkedOverride" @change="updateDaysWorkedOverride" :readonly="readonly || daysWorkedLocked" ></div>
            <div :class="[ showCoding ? 'col-sm-2' : 'col-sm-5', isMatchingAmount ? '' : 'overridden' ]" style="padding-right: 0px"><input class="form-control text-right" type="text" :name="modelName + 'Amount'" placeholder="Amount" :value="adjustedAmount" @change="updateAmountOverride" :readonly="readonly" ></div>
        </div>

        <div v-if="showCoding">
            <div v-if="enableProBooks">
                <div class="col-sm-2" style="padding-right: 0px">
                    <autocomplete :options="probooksAccounts" :name="codeName" v-model="allowanceCode" :ref="'assignment.' + codeName" :preselect="preselectedCode" :display="'code'" :readonly="readonly" @select="updateCode" placeholder="Acct" :default-show="true" :highlight="-1">
                        <template v-slot:item="option">
                            <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                        </template>
                    </autocomplete>
                </div>
                <div v-if="isWeekly">
                    <div class="col-sm-2 timecard" style="padding-right: 0px">
                        <autocomplete :options="probooksFreeFields" :name="ffName" v-model="allowanceFreeField" :ref="'assignment.' + ffName" :display="'code'" :preselect="preselectedFreeField" :readonly="readonly" @select="updateFreeField" placeholder="F1" :min-keyword-length="0" :default-show="true" :highlight="-1">
                            <template v-slot:item="option">
                                <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                            </template>
                        </autocomplete>
                    </div>
                    <div class="col-sm-2 timecard" style="padding-right: 0px">
                        <autocomplete :options="probooksFreeFields" :name="ff2Name" v-model="allowanceFreeField2" :ref="'assignment.' + ffName" :display="'code'" :preselect="preselectedFreeField2" :readonly="readonly" @select="updateFreeField2" placeholder="F2" :min-keyword-length="0" :default-show="true" :highlight="-1">
                            <template v-slot:item="option">
                                <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                            </template>
                        </autocomplete>
                    </div> 
                </div>
                <div v-else>
                    <div class="col-sm-3 timecard" style="padding-right: 0px">
                        <div class="col-xs-6 timecard" style="padding-right: 0px">
                            <autocomplete :options="probooksFreeFields" :name="ffName" v-model="allowanceFreeField" :ref="'assignment.' + ffName" :display="'code'" :preselect="preselectedFreeField" :readonly="readonly" @select="updateFreeField" placeholder="F1" :min-keyword-length="0" :default-show="true" :highlight="-1">
                                <template v-slot:item="option">
                                    <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                </template>
                            </autocomplete>
                        </div>
                        <div class="col-xs-6 timecard" style="padding-right: 0px">
                            <autocomplete :options="probooksFreeFields" :name="ff2Name" v-model="allowanceFreeField2" :ref="'assignment.' + ffName" :display="'code'" :preselect="preselectedFreeField2" :readonly="readonly" @select="updateFreeField2" placeholder="F2" :min-keyword-length="0" :default-show="true" :highlight="-1">
                                <template v-slot:item="option">
                                    <p><strong>{{ option.code + '-' + option.itemname }}</strong></p>
                                </template>
                            </autocomplete>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else>
                <div class="col-sm-2" style="padding-right: 0px"><input class="form-control" type="text" :name="modelName + '_code'" placeholder="Acct" :value="assignment[codeName]" :readonly="readonly" @change="updateCode"></div>
                <div v-if="isWeekly">
                    <div class="col-sm-2 timecard" style="padding-right: 0px"><input class="form-control" type="text" :name="modelName + '_free_field'" :value="assignment[ffName]" placeholder="F1" :readonly="readonly" @change="updateFreeField"></div>
                    <div class="col-sm-2 timecard" style="padding-right: 0px"><input class="form-control" type="text" :name="modelName + '_free_field2'" :value="assignment[ff2Name]" placeholder="F2" :readonly="readonly" @change="updateFreeField2"></div>
                </div>
                <div v-else>
                    <div class="col-sm-3 timecard" style="padding-right: 0px">
                        <div class="col-xs-6 timecard" style="padding-right: 0px"><input class="form-control" type="text" :name="modelName + '_free_field'" :value="assignment[ffName]" placeholder="F1" :readonly="readonly" @change="updateFreeField"></div>
                        <div class="col-xs-6 timecard" style="padding-right: 0px"><input class="form-control" type="text" :name="modelName + '_free_field2'" :value="assignment[ff2Name]" placeholder="F2" :readonly="readonly" @change="updateFreeField2"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div class="clear"></div>
</div>
</template>

<style scoped>
.text-right { text-align: right !important; padding-right: 5px; }

.overridden {
    border: 1px solid transparent;
    border-radius: 5px;
    border-color: #FF00FF70;
}

</style>